
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { isLandlord } from '@/utils/roleHelpers';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import HeroSection from '../components/hero/HeroSection';
import AIStatsSection from '../components/stats/AIStatsSection';
import ProblemsSection from '../components/problems/ProblemsSection';
import ExperienceSection from '../components/experience/ExperienceSection';
import LandlordSection from '../components/landlord/LandlordSection';
import CTASection from '../components/cta/CTASection';
import JsonLd from '../components/SEO/JsonLd';

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isInitialized } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [authProcessing, setAuthProcessing] = useState(false);
  const [isConfirmingEmail, setIsConfirmingEmail] = useState(false);
  
  // Hero images - keeping vibrant and bright
  const heroImages = [
    '/lovable-uploads/49baeb2c-dad4-4893-a4c8-f06971fac101.png',
    '/lovable-uploads/036c702f-9450-4758-8105-bf4f2c2cf3c5.png',
    '/lovable-uploads/21767fd8-04ff-4dfb-9104-72c52bebd70b.png'
  ];

  // Handle URL hash authentication tokens from email confirmation
  useEffect(() => {
    const handleAuthTokens = async () => {
      const hash = window.location.hash;
      
      if (hash && hash.includes('access_token') && !authProcessing) {
        console.log('[Auth] Processing email confirmation tokens');
        console.log('[Auth] Full hash:', hash);
        setAuthProcessing(true);
        setIsConfirmingEmail(true);
        
        try {
          // Extract tokens from URL hash - handle both # and & separators
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const tokenType = hashParams.get('type');
          
          console.log('[Auth] Extracted tokens:', { 
            hasAccessToken: !!accessToken, 
            hasRefreshToken: !!refreshToken,
            tokenType 
          });
          
          if (accessToken && refreshToken) {
            console.log('[Auth] Setting session from email confirmation');
            
            // Set the session using the tokens
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('[Auth] Error setting session:', error);
            } else {
              console.log('[Auth] Session set successfully from email confirmation');
              console.log('[Auth] User data:', data.user);
              
              // Clear the hash from URL immediately
              window.history.replaceState({}, document.title, window.location.pathname);
              
              // If this is a signup confirmation, set the new signup flag
              if (tokenType === 'signup') {
                console.log('[Auth] Setting new signup flag');
                localStorage.setItem('isNewSignup', 'true');
              }
            }
          } else {
            console.warn('[Auth] Missing required tokens for session setup');
          }
        } catch (error) {
          console.error('[Auth] Error processing auth tokens:', error);
        } finally {
          setAuthProcessing(false);
          setIsConfirmingEmail(false);
        }
      }
    };

    handleAuthTokens();
  }, [location.hash, authProcessing]);

  // Navigate users to appropriate dashboard once authenticated
  useEffect(() => {
    if (isInitialized && user && profile && !authProcessing && !isConfirmingEmail) {
      console.log('[Index] Redirecting authenticated user:', {
        userId: user.id,
        email: user.email
      });
      
      // Add a longer delay to ensure all auth state is settled and avoid race conditions
      const redirectTimer = setTimeout(() => {
        try {
          if (isLandlord(profile)) {
            console.log('[Index] Navigating to landlord dashboard');
            navigate('/landlord-dashboard', { replace: true });
          } else {
            console.log('[Index] Navigating to renter dashboard');
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('[Index] Error during navigation:', error);
          // Fallback to dashboard if there's an error
          navigate('/dashboard', { replace: true });
        }
      }, 300);

      return () => clearTimeout(redirectTimer);
    }
  }, [user, profile, isInitialized, navigate, authProcessing, isConfirmingEmail]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Show loading state while processing authentication or not initialized
  if (!isInitialized || authProcessing || isConfirmingEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <img 
              src="/lovable-uploads/76503acc-0290-4b3e-ae3b-f6ab214ab2ce.png" 
              alt="Flamingo" 
              className="h-10 mx-auto mb-4"
            />
          </div>
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300">
            {isConfirmingEmail ? 'Confirming your email...' : 'Loading Flamingo...'}
          </p>
        </div>
      </div>
    );
  }

  // If user is authenticated, they should be redirected by the effect above
  // Show landing page for non-authenticated users
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      {/* JSON-LD: Organization and Website */}
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Flamingo',
        url: window.location.origin,
        logo: `${window.location.origin}/favicon.ico`,
        sameAs: ['https://www.linkedin.com/company/', 'https://twitter.com/']
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Flamingo',
        url: window.location.origin,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${window.location.origin}/discover?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      }} />

      <HeroSection heroImages={heroImages} currentImageIndex={currentImageIndex} />

      <AIStatsSection />

      <ProblemsSection />

      <ExperienceSection />

      <LandlordSection />

      <CTASection />

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
};

export default Index;
